/**
 * Animate Section Component
 * Automatically triggers fade-in and slide-up animation when elements with 
 * class "animate-section" enter the viewport
 */

(function() {
  'use strict';

  class AnimateSection {
    constructor() {
      this.animateClass = 'animate-section';
      this.activeClass = 'animate-active';
      this.showClass = 'animate-show';
      this.observer = null;
      this.lastScrollY = window.pageYOffset || document.documentElement.scrollTop;
      this.isScrollingDown = true;
      this.init();
    }

    init() {
      // Check if Intersection Observer is supported
      if (!('IntersectionObserver' in window)) {
        console.warn('Intersection Observer not supported. Showing elements without animation.');
        this.fallback();
        return;
      }

      this.setupScrollListener();
      this.setupObserver();
      this.observeElements();
    }

    setupScrollListener() {
      // Track scroll direction
      let ticking = false;
      
      window.addEventListener('scroll', () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
            this.isScrollingDown = currentScrollY > this.lastScrollY;
            this.lastScrollY = currentScrollY;
            ticking = false;
          });
          ticking = true;
        }
      }, { passive: true });
    }

    setupObserver() {
      const options = {
        root: null, // Use viewport
        rootMargin: '0px 0px -50px 0px', // Trigger 50px before element enters viewport
        threshold: 0.1 // Trigger when 10% of element is visible
      };

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (this.isScrollingDown) {
              // Scrolling down: trigger animation
              entry.target.classList.remove(this.showClass);
              entry.target.classList.add(this.activeClass);
            } else {
              // Scrolling up: show without animation
              entry.target.classList.remove(this.activeClass);
              entry.target.classList.add(this.showClass);
            }
          } else {
            // Element leaving viewport: reset to hidden state
            entry.target.classList.remove(this.activeClass);
            entry.target.classList.remove(this.showClass);
          }
        });
      }, options);
    }

    observeElements() {
      const elements = document.querySelectorAll(`.${this.animateClass}`);
      
      elements.forEach(element => {
        // Observe all elements, allowing repeated animations
        this.observer.observe(element);
      });
    }

    fallback() {
      // For browsers without Intersection Observer support
      const elements = document.querySelectorAll(`.${this.animateClass}`);
      elements.forEach(element => {
        element.classList.add(this.activeClass);
      });
    }

    // Public method to refresh observer for dynamically loaded content
    refresh() {
      if (this.observer) {
        this.observeElements();
      }
    }

    // Public method to destroy the observer
    destroy() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  // Initialize when DOM is ready
  function initAnimateSection() {
    window.animateSectionInstance = new AnimateSection();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAnimateSection);
  } else {
    initAnimateSection();
  }

  // Re-observe elements when Shopify sections are loaded/reloaded in theme editor
  document.addEventListener('shopify:section:load', function() {
    if (window.animateSectionInstance) {
      setTimeout(function() {
        window.animateSectionInstance.refresh();
      }, 100);
    }
  });

  // Expose class for manual usage if needed
  window.AnimateSection = AnimateSection;
})();
